import { useState, useCallback, useLayoutEffect, useRef } from "react";
import type { NodePosition, ExpandedMap } from "../types";

function getAbsoluteOffset(el: HTMLElement, container: HTMLElement) {
  let x = 0;
  let y = 0;
  let current: HTMLElement | null = el;
  while (current && current !== container) {
    x += current.offsetLeft;
    y += current.offsetTop;
    current = current.offsetParent as HTMLElement | null;
  }
  return { x, y };
}

export function useNodePositions(
  containerRef: React.RefObject<HTMLElement | null>,
  expanded: ExpandedMap
) {
  const [positions, setPositions] = useState<Record<string, NodePosition>>({});
  const frameRef = useRef<number>(0);

  const measure = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    const nodes = container.querySelectorAll<HTMLElement>("[data-node-id]");
    const pos: Record<string, NodePosition> = {};

    nodes.forEach((el) => {
      const id = el.getAttribute("data-node-id");
      if (!id) return;

      const { x, y } = getAbsoluteOffset(el, container);
      const w = el.offsetWidth;
      const h = el.offsetHeight;

      pos[id] = {
        left: { x, y: y + h / 2 },
        right: { x: x + w, y: y + h / 2 },
      };
    });

    setPositions(pos);
  }, [containerRef]);

  useLayoutEffect(() => {
    // Measure at multiple intervals to catch CSS transitions
    const delays = [0, 50, 150, 310];
    const timers = delays.map((d) =>
      window.setTimeout(() => {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = requestAnimationFrame(measure);
      }, d)
    );
    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(frameRef.current);
    };
  }, [measure, expanded]);

  return positions;
}
